/*
 * Katana Story — smooth scroll-scrubbed frame sequence.
 * Preloads a set of frame images and draws the one matching scroll progress to
 * a <canvas> (cover-fit). Far smoother than seeking a <video> on scroll.
 *
 * - Separate desktop / mobile sequences (chosen by viewport width).
 * - Keyframe text: soft-fades the narrative to each keyframe's text as scroll
 *   progress passes it (based on progress, so it stays consistent across the
 *   desktop/mobile sequences even if their frame counts differ).
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 760px)';

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initSection(section) {
    var canvas = section.querySelector('[data-kstory-canvas]');
    if (!canvas) return;

    var desktopUrls = parseJSON(section.querySelector('[data-kstory-frames]')) || [];
    var mobileUrls = parseJSON(section.querySelector('[data-kstory-frames-mobile]')) || [];
    var keyframes = parseJSON(section.querySelector('[data-kstory-keyframes]')) || [];
    if (!desktopUrls.length && !mobileUrls.length) return;

    // Keyframes sorted by frame; thresholds are stored as scroll-progress
    // fractions off a reference count so they line up on either sequence.
    var refCount = desktopUrls.length || mobileUrls.length;
    keyframes = keyframes
      .filter(function (k) { return k && k.text; })
      .sort(function (a, b) { return a.frame - b.frame; });
    keyframes.forEach(function (k) {
      k.p = refCount > 1 ? (k.frame - 1) / (refCount - 1) : 0;
    });

    var narrative = section.querySelector('[data-kstory-narrative]');
    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    var urls = [];
    var images = [];
    var current = -1;
    var usingMobile = null;
    // Seeded from the server-rendered opening line so it isn't faded out and
    // straight back in on the first scroll tick.
    var currentText = narrative ? narrative.textContent.trim() || null : null;
    var fadeTimer = null;

    function sizeCanvas() {
      var rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }

    function drawImageCover(img) {
      if (!img || !img.width) return;
      var cw = canvas.width;
      var ch = canvas.height;
      var ir = img.width / img.height;
      var cr = cw / ch;
      var dw, dh, dx, dy;
      if (ir > cr) {
        dh = ch;
        dw = ch * ir;
        dx = (cw - dw) / 2;
        dy = 0;
      } else {
        dw = cw;
        dh = cw / ir;
        dx = 0;
        dy = (ch - dh) / 2;
      }
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, dx, dy, dw, dh);
    }

    function progress() {
      var rect = section.getBoundingClientRect();
      var total = section.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      var scrolled = Math.min(Math.max(-rect.top, 0), total);
      return scrolled / total;
    }

    // Soft fade the narrative to a new text (out, swap, in).
    function setNarrative(text) {
      if (!narrative || text === currentText) return;
      currentText = text;
      narrative.classList.remove('is-shown');
      clearTimeout(fadeTimer);
      fadeTimer = setTimeout(function () {
        narrative.textContent = text || '';
        if (text) narrative.classList.add('is-shown');
      }, 300);
    }

    function updateNarrative(p) {
      var active = null;
      for (var i = 0; i < keyframes.length; i++) {
        if (p >= keyframes[i].p) active = keyframes[i];
        else break;
      }
      setNarrative(active ? active.text : '');
    }

    function render() {
      var p = progress();
      updateNarrative(p);
      if (!urls.length) return;
      var idx = Math.round(p * (urls.length - 1));
      if (idx < 0) idx = 0;
      if (idx > urls.length - 1) idx = urls.length - 1;
      if (idx === current) return;
      var img = images[idx];
      if (img && img.complete && img.naturalWidth) {
        current = idx;
        drawImageCover(img);
      } else if (img) {
        img.addEventListener(
          'load',
          function () {
            var again = Math.round(progress() * (urls.length - 1));
            if (again === idx) {
              current = idx;
              drawImageCover(img);
            }
          },
          { once: true }
        );
      }
    }

    // Load (and preload) the frame set for the current viewport.
    function loadSet() {
      var wantMobile = mobileUrls.length > 0 && window.matchMedia(MOBILE_MQ).matches;
      if (wantMobile === usingMobile && urls.length) return;
      usingMobile = wantMobile;
      urls = wantMobile ? mobileUrls : desktopUrls;
      images = new Array(urls.length);
      current = -1;
      urls.forEach(function (url, i) {
        var img = new Image();
        img.decoding = 'async';
        img.src = url;
        images[i] = img;
        if (i === 0) {
          img.addEventListener('load', function () {
            sizeCanvas();
            render();
          });
        }
      });
      sizeCanvas();
      render();
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        render();
        ticking = false;
      });
    }

    loadSet();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      loadSet(); // swap sequence if we crossed the breakpoint
      sizeCanvas();
      current = -1;
      render();
    });
  }

  function boot() {
    var sections = document.querySelectorAll('[data-kstory]');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
